import { useEffect } from 'react'

/** Convierte URL YouTube (watch / youtu.be / embed / shorts) a URL embebible. */
export function youtubeUrlToEmbed(url: string): string {
  if (!url || typeof url !== 'string') return ''
  const raw = url.trim()
  if (!raw) return ''
  try {
    const parsed = new URL(raw)
    const host = parsed.hostname.replace(/^www\./, '').toLowerCase()
    if (host === 'youtu.be') {
      const id = parsed.pathname.replace(/^\//, '').split('/')[0]
      return id ? `https://www.youtube-nocookie.com/embed/${id}?rel=0&playsinline=1` : ''
    }
    if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com') {
      const v = parsed.searchParams.get('v')
      if (v) return `https://www.youtube-nocookie.com/embed/${v}?rel=0&playsinline=1`
      const shorts = parsed.pathname.match(/^\/shorts\/([^/]+)/)
      if (shorts) return `https://www.youtube-nocookie.com/embed/${shorts[1]}?rel=0&playsinline=1`
      const embed = parsed.pathname.match(/^\/embed\/([^/]+)/)
      if (embed) return `https://www.youtube-nocookie.com/embed/${embed[1]}?rel=0&playsinline=1`
    }
  } catch {
    return ''
  }
  return ''
}

type Props = {
  open: boolean
  title: string
  videoUrl: string
  onClose: () => void
}

/**
 * Modal de video YouTube dentro de Food Plan (paridad con video de bienvenida D28D).
 */
export default function YoutubeVideoModal({ open, title, videoUrl, onClose }: Props) {
  const embed = youtubeUrlToEmbed(videoUrl)

  useEffect(() => {
    if (!open) return undefined
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-[200] flex items-start sm:items-center justify-center p-3 sm:p-4 bg-black/60 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl bg-white border border-gray-200 rounded-2xl shadow-2xl max-h-[min(92dvh,calc(100dvh-1.5rem))] overflow-y-auto my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-gray-100 sticky top-0 bg-white z-10">
          <h3 className="font-semibold text-gray-900 min-w-0 break-words text-sm sm:text-base">{title}</h3>
          <button
            type="button"
            className="shrink-0 px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50"
            onClick={onClose}
          >
            Cerrar
          </button>
        </div>
        <div className="p-4 space-y-3">
          {embed ? (
            <div className="relative w-full aspect-video rounded-xl overflow-hidden bg-black">
              <iframe
                title={title}
                src={embed}
                className="absolute inset-0 w-full h-full border-0"
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>
          ) : (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl p-3">
              No se pudo incrustar el video. Ábrelo en una pestaña nueva.
            </p>
          )}
          {videoUrl ? (
            <a
              href={videoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-gray-500 hover:underline break-all inline-block"
            >
              Abrir en YouTube
            </a>
          ) : null}
        </div>
      </div>
    </div>
  )
}
